import React, { useCallback, useMemo } from "react";

import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { useParams } from "react-router-dom";

import { getViewedContentHref, viewsColumns } from "./tableParams/viewsColumns";
import StatsAPI from "../../../api/StatsAPI";
import { getElementsByIndex, getIcon } from "../../../utils/data/Content";
import DataTable from "../../dataTable/DataTable";
import {
  Card,
  CardAction,
  CardContent,
  CardHeader,
  CardTitle,
} from "../../ui/card";
import ExportCsvButton from "../charts/ExportCsvButton";

import type { JSX } from "react";

import type { AnalyticsPeriod } from "@/src/hooks/usePeriodFilter";

import type { ViewedContentRow } from "./tableParams/viewsColumns.types";
import type { GetMediasItem } from "../../../api/MediaAPI.types";
import type { AppState } from "../../../reducers";

interface ViewsTableProps {
  userUuid: string;
  contentAbility?: boolean;
  period: AnalyticsPeriod;
}

function getViewId(row: ViewedContentRow): string {
  return row.uuid;
}

function ViewsTable({
  userUuid,
  contentAbility = true,
  period,
}: ViewsTableProps): JSX.Element {
  const { projectSlug } = useParams<{ projectSlug: string }>();
  const { t } = useTranslation("editor-analytics");
  const contents = useSelector(
    (state: AppState) => state.fetchReducer.contents,
  );

  const { startDate, endDate } = period;

  const getUserWatchedQuery = StatsAPI.generateQuery({
    fn: StatsAPI.getUserWatched,
    params: [{ userId: userUuid, startDate, endDate, projectSlug }],
  });

  const {
    data: watchedData,
    isLoading,
    isError,
    refetch,
  } = useQuery({
    ...getUserWatchedQuery,
    enabled: !!userUuid,
  });

  const rowList = useMemo(
    (): ViewedContentRow[] =>
      (watchedData?.data ?? []).map((item) => {
        const media: GetMediasItem | undefined = getElementsByIndex(
          item.media_uuid,
          contents,
          "uuid",
        )?.[0];

        return {
          uuid: item.uuid,
          media_uuid: item.media_uuid,
          content_status: media?.publication_status ?? "",
          content_type: media ? getIcon(media) : "",
          media_type: media?.type ?? "",
          media_label: media?.label ?? "",
          platform: item.platform,
          media_duration: item.media_duration,
          playtime: item.playtime,
          play_percentage: item.play_percentage,
          datetime: item.datetime,
        };
      }),
    [watchedData?.data, contents],
  );

  const columnList = useMemo(
    () => viewsColumns({ t, contentAbility }),
    [t, contentAbility],
  );

  const getRowHref = useCallback(
    (row: ViewedContentRow): string | undefined =>
      getViewedContentHref(row, contentAbility),
    [contentAbility],
  );

  const canExport = !isLoading && rowList.length > 0;

  const fetchExport = async (): Promise<string> =>
    StatsAPI.getUserWatched({
      userId: userUuid,
      startDate,
      endDate,
      isExport: true,
      projectSlug,
    });

  return (
    <Card className="gap-4">
      <CardHeader>
        <CardTitle>{t("viewsTitle")}</CardTitle>
        <CardAction className="flex items-center gap-2">
          <ExportCsvButton
            disabled={!canExport}
            fetchCsv={fetchExport}
            fileName="watched_medias.csv"
          />
        </CardAction>
      </CardHeader>
      <CardContent>
        <DataTable
          columns={columnList}
          data={rowList}
          emptyText={t("noDatas")}
          getRowHref={getRowHref}
          getRowId={getViewId}
          isError={isError}
          isLoading={isLoading}
          onRetry={refetch}
          resetPageOn={[startDate.valueOf(), endDate.valueOf()]}
          urlSync={{ prefix: "views" }}
        />
      </CardContent>
    </Card>
  );
}

export default ViewsTable;
